import axios from "axios";
import { Box, Heading, HStack, VStack } from "native-base";
import React, { useState } from "react";
import { StyleSheet } from "react-native";

import Theme from "../argon-ui/Theme";
import CountDown from "../components/CountDown";
import { translate } from "../locales/translate";
import InputBase from "../nativeBase-ui/InputBase";
import Text from "./TextFont";
import VirtualKeyboard from "./VirtualKeyboard";

interface PinCodeModel {
  queueId: string;
  delay: number;
  areaId: number;
  areaUId: string;
  areaName: string;
  pinCode?: string;
}
interface PostPinCodeModel {
  queueId: string;
  areaId: number;
  areaUId: string;
  buildingId: string;
  pinCode?: string;
}

type Props = {
  content: PinCodeModel;
  accessToken: string;
  buildingId: string;
  serverUrl: string;
  callback: () => void;
  toastMessageCallback: (message: any) => void;
};
const INPUT_HEIGHT = 50;

const PinCodeArm = (props: Props) => {
  const {
    buildingId,
    callback,
    serverUrl,
    content,
    accessToken,
    toastMessageCallback,
  } = props;
  const { areaId, areaUId, areaName, delay, queueId } = content;
  // === Props ===
  const [loading, setLoading] = useState<boolean>(false);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(
    null
  );

  const [keyboardValue, setKeyboardValue] = useState<string>("");
  const [codeError, setCodeError] = useState({
    hasError: false,
    errorMessage: "",
  });

  //   === Methods ===
  const changeTextVirtualKeyboardHandler = (value: string) => {
    codeError.hasError && resetErrorHandler();
    setKeyboardValue(value);
  };
  const resetErrorHandler = () => {
    setCodeError({
      hasError: false,
      errorMessage: "",
    });
  };
  const validationCode = () => {
    let isValid = true;
    if (keyboardValue.length == 0) {
      isValid = false;
      setCodeError({
        hasError: true,
        errorMessage: translate("touchPanel.errors.pinCodeIsRequired"),
      });
    }
    return isValid;
  };
  // === Submit ===
  const submitHandler = () => {
    if (validationCode()) {
      //   console.log("keyboardValue", keyboardValue);
      setLoading(true);
      portHttpHandler();
    }
  };

  const portHttpHandler = () => {
    let _serverUrl = serverUrl;
    if (!_serverUrl?.endsWith("/")) {
      _serverUrl += "/";
    }
    const model: PostPinCodeModel = {
      areaId,
      areaUId,
      buildingId,
      queueId,
      pinCode: keyboardValue,
    };
    try {
      axios
        .post(`${_serverUrl}Areas/EnterPin`, model, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        })
        .then((res) => {
          setLoading(false);
          const { result, remainingAttempts } = res.data;
          setKeyboardValue("");
          if (result) {
            callback && callback();
            const toastMessage = {
              title: translate("touchPanel.successAction"),
              variant: "top-accent",
              status: "success",
            };
            toastMessageCallback(toastMessage);
          } else {
            setRemainingAttempts(remainingAttempts);
            const toastMessage = {
              title: translate("touchPanel.errors.errorPinCodeMessage"),
              variant: "top-accent",
              status: "error",
            };
            toastMessageCallback(toastMessage);
          }
        })
        .catch((error) => {
          console.log("error post pin code touch panel", error);
          setLoading(false);
          setKeyboardValue("");
          const toastMessage = {
            title: translate("login.errors.undefinedError"),
            variant: "top-accent",
            status: "error",
          };
          toastMessageCallback(toastMessage);
        });
    } catch (error) {
      console.log("error post pin code touch panel", error);
      setLoading(false);
      const toastMessage = {
        title: translate("login.errors.undefinedError"),
        variant: "top-accent",
        status: "error",
      };
      toastMessageCallback(toastMessage);
    }
  };
  return (
    <VStack space="2" style={styles.container} justifyContent="flex-end">
      {/* === input box === */}
      <Box
        width={{
          base: "100%",
        }}
        style={{
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Heading mb="3" size="md">
          {areaName}
        </Heading>
        <Text style={{ textFont: 14, color: Theme.COLORS.GRAY_DARK }}>
          {translate("touchPanel.pinCodeDes")}
        </Text>
        {/* === Countdown delay => second  */}
        <HStack alignItems="center" style={{ direction: "rtl" }}>
          <CountDown
            until={delay}
            size={20}
            digitStyle={{
              backgroundColor: "#FFFFFF00",
              paddingTop: 15,
            }}
            timeLabelStyle={{ color: "grey" }}
            timeToShow={["M", "S"]}
            timeLabels={{
              s: " ",
              m: " ",
              //   translate("timer.seconds")
            }}
            onFinish={() => callback && callback()}
            showSeparator
          />
          <Text style={{ color: Theme.COLORS.MUTED, fontSize: 12 }}>
            {translate("touchPanel.alarmPinCode")}
          </Text>
        </HStack>
        {remainingAttempts && (
          <HStack alignItems="center" style={{ direction: "rtl" }}>
            <Text style={{ textFont: 15, color: Theme.COLORS.ERROR }}>
              {translate("touchPanel.remainingAttempts") + " : "}
            </Text>
            <Text style={{ textFont: 15, color: Theme.COLORS.ERROR }}>
              {" " + remainingAttempts}
            </Text>
            <Text style={{ textFont: 15, color: Theme.COLORS.ERROR }}>
              {translate("app.times")}
            </Text>
          </HStack>
        )}
      </Box>
      {/* === Numeric Keyboard ===  */}
      <Box
        width={{
          base: "100%",
          // md: "50%",
        }}
      >
        {/* === Pin Code Input === */}
        <InputBase
          showSoftInputOnFocus={false}
          inputStyle={{
            height: INPUT_HEIGHT,
            textAlign: "center",
            with: "100%",
            fontSize: 16,
          }}
          showSubInput={true}
          keyboardType="numeric"
          isInvalid={codeError.hasError}
          errorMessage={codeError.errorMessage}
          placeholder={translate("touchPanel.pinCode")}
          onChangeText={(txt: string) => {
            console.log("txt", txt);
            setKeyboardValue(txt);
          }}
          value={keyboardValue}
        />
        <VirtualKeyboard
          rowStyle={{ width: "100%" }}
          color={Theme.COLORS.MUTED}
          pressMode="string"
          onPress={(val: string) => changeTextVirtualKeyboardHandler(val)}
          onSubmit={submitHandler}
          submitLoading={loading}
          textStyle={{ fontFamily: "IRANSansMobile" }}
        />
      </Box>
    </VStack>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 20,
  },
});
export default PinCodeArm;
